import { useCallback, useEffect, useRef, useState } from "react";
import { StaticEngine, RadioBank, speak, speakInvocation, cancelInvocation } from "../lib/audio";
import { wordsFor, pick } from "../lib/words";

const now = () => new Date().toLocaleTimeString("it-IT");
let seq = 0;
const uid = () => `${Date.now()}-${seq++}`;

const JUMP_CHANCE = 0.06;
const nextFreq = (f) => {
  if (Math.random() < JUMP_CHANCE) return Math.round((87.5 + Math.random() * 20.5) * 10) / 10;
  const n = f + (Math.random() < 0.5 ? 0.1 : 0.2);
  return n > 108 ? 87.5 : Math.round(n * 10) / 10;
};
const MAX_SILENCE_MS = 22000;
const WORDS_PER_SEC = 0.06;
const BOOST_PER_SEC = 0.12;
const freshState = () => ({ freq: 87.5, boost: 0, speaking: false, lastQ: null, lastWordAt: Date.now() });
const makeWord = (s, lang) => ({ id: uid(), word: pick(wordsFor(lang)), freq: s.freq, time: now(), question: s.lastQ });

const sweepTick = (s, radio, engine, volume) => {
  s.freq = nextFreq(s.freq);
  const hit = radio.tune(s.freq, volume, s.speaking);
  if (hit) engine.duck(true);
  else if (!s.speaking) engine.tick(s.freq);
  return hit;
};

const useAudioEngines = (volume) => {
  const engine = useRef(new StaticEngine());
  const radio = useRef(new RadioBank());
  useEffect(() => engine.current.setVolume(volume), [volume]);
  useEffect(() => {
    const eng = engine.current;
    const rad = radio.current;
    return () => { cancelInvocation(); window.speechSynthesis?.cancel(); eng.stop(); rad.stop(); };
  }, []);
  return { engine, radio };
};

const useSweep = ({ active, speed, state, engine, radio, settings, receive, onTick }) => {
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      const s = state.current;
      const hit = sweepTick(s, radio.current, engine.current, settings.current.volume);
      onTick.current(s.freq, hit);
      const overdue = Date.now() - s.lastWordAt > MAX_SILENCE_MS;
      if (!s.speaking && (overdue || Math.random() < ((WORDS_PER_SEC + s.boost * BOOST_PER_SEC) * speed) / 1000)) receive();
    }, speed);
    return () => clearInterval(id);
  }, [active, speed, receive, state, engine, radio, settings, onTick]);
};

export const useGhostAudio = ({ speed, volume, voice, lang, tts }) => {
  const [active, setActive] = useState(false);
  const [freq, setFreq] = useState(87.5);
  const [tuned, setTuned] = useState(null);
  const [word, setWord] = useState(null);
  const [log, setLog] = useState([]);
  const { engine, radio } = useAudioEngines(volume);
  const state = useRef(freshState());
  const settings = useRef({ volume, voice, lang, tts });
  settings.current = { volume, voice, lang, tts };
  const onTick = useRef(null);
  onTick.current = (f, hit) => { setFreq(f); setTuned(hit ? hit.name : null); };

  const receive = useCallback(() => {
    const s = state.current;
    const w = makeWord(s, settings.current.lang);
    s.lastWordAt = Date.now();
    setWord(w);
    setLog((l) => [...l, w]);
    s.boost = Math.max(0, s.boost - 1);
    if (!settings.current.voice) return;
    s.speaking = true;
    engine.current.duck(true);
    speak(w.word, settings.current.volume, () => { s.speaking = false; engine.current.duck(false); }, settings.current.tts);
  }, [engine]);

  useSweep({ active, speed, state, engine, radio, settings, receive, onTick });

  const startAudio = (stations, invocation) => {
    const ok = engine.current.start(settings.current.volume);
    radio.current.start(stations, engine.current);
    window.speechSynthesis?.cancel();
    setLog([]);
    setWord(null);
    state.current = freshState();
    if (invocation) {
      state.current.speaking = true;
      engine.current.duck(true);
      speakInvocation(invocation, settings.current.volume, () => {
        state.current.speaking = false;
        state.current.lastWordAt = Date.now();
        engine.current.duck(false);
      }, settings.current.tts);
    }
    setActive(true);
    return ok;
  };

  const stopAudio = () => {
    cancelInvocation();
    setActive(false);
    engine.current.stop();
    radio.current.stop();
    setTuned(null);
    window.speechSynthesis?.cancel();
  };

  const ask = (q) => {
    state.current.boost = 3;
    state.current.lastQ = q;
    setLog((l) => [...l, { id: uid(), type: "q", text: q, time: now() }]);
  };

  return { active, freq, tuned, word, log, engine, startAudio, stopAudio, ask };
};
